import {
  BudgetCategory,
  BudgetI,
  BudgetItem,
  BudgetSubCategory,
} from "components/dashboard/body/projects/panels/budget/types";

export type BudgetSummaryCache = { budget: BudgetI | null; budgetId: string | undefined };

/** Nest `{ data: T }` responses from the budgeting API into a plain entity. */
export function unwrapBudgetApiData<T>(payload: T | { data: T }): T {
  if (
    payload !== null &&
    typeof payload === "object" &&
    "data" in payload &&
    (payload as { data: T }).data !== undefined &&
    (payload as { data: T }).data !== null
  ) {
    return (payload as { data: T }).data;
  }
  return payload as T;
}

export function addBudgetSubCategoryToCache(
  cache: BudgetSummaryCache | undefined,
  categoryId: number,
  subCategory: BudgetSubCategory
): BudgetSummaryCache | undefined {
  if (!cache?.budget) return cache;

  const budget = structuredClone(cache.budget);
  const category = budget.categories.find((entry) => entry.budget_category_id === categoryId);
  if (!category) return cache;

  const normalized: BudgetSubCategory = {
    ...subCategory,
    items: subCategory.items ?? [],
    importance: subCategory.importance ?? null,
    locked: subCategory.locked ?? false,
  };

  const existingIndex = category.sub_categories.findIndex(
    (entry) => entry.budget_sub_category_id === normalized.budget_sub_category_id
  );
  if (existingIndex >= 0) {
    category.sub_categories[existingIndex] = normalized;
  } else {
    category.sub_categories = [...category.sub_categories, normalized];
  }

  return { ...cache, budget };
}

export function addBudgetCategoryToCache(
  cache: BudgetSummaryCache | undefined,
  category: BudgetCategory
): BudgetSummaryCache | undefined {
  if (!cache?.budget) return cache;

  const budget = structuredClone(cache.budget);
  const normalized: BudgetCategory = {
    ...category,
    sub_categories: category.sub_categories ?? [],
  };

  const existingIndex = budget.categories.findIndex(
    (entry) => entry.budget_category_id === normalized.budget_category_id
  );
  if (existingIndex >= 0) {
    budget.categories[existingIndex] = normalized;
  } else {
    budget.categories = [...budget.categories, normalized];
  }

  return { ...cache, budget };
}

function applyLockedToItemTree(items: BudgetItem[], locked: boolean): void {
  for (const item of items) {
    item.locked = locked;
    if (item.children?.length) {
      applyLockedToItemTree(item.children, locked);
    }
  }
}

export function patchBudgetSubCategoryInCache(
  cache: BudgetSummaryCache | undefined,
  subCategoryId: number,
  patch: Partial<BudgetSubCategory>
): BudgetSummaryCache | undefined {
  if (!cache?.budget) return cache;

  const budget = structuredClone(cache.budget);
  for (const category of budget.categories) {
    const subCategory = category.sub_categories.find(
      (entry) => entry.budget_sub_category_id === subCategoryId
    );
    if (!subCategory) continue;

    Object.assign(subCategory, patch);

    if (patch.locked !== undefined) {
      applyLockedToItemTree(subCategory.items, patch.locked);
    }

    return { ...cache, budget };
  }

  return cache;
}

/** A copy of the categories with one line patched, or the same array when no line matches. */
export function patchBudgetItemInCategories(
  categories: BudgetCategory[],
  itemId: number,
  patch: Partial<BudgetItem>
): BudgetCategory[] {
  const patched = structuredClone(categories);
  for (const category of patched) {
    for (const subCategory of category.sub_categories) {
      const item = findItemInTree(subCategory.items, itemId);
      if (item) {
        Object.assign(item, patch);
        return patched;
      }
    }
  }
  return categories;
}

export function patchBudgetItemInCache(
  cache: BudgetSummaryCache | undefined,
  itemId: number,
  patch: Partial<BudgetItem>
): BudgetSummaryCache | undefined {
  if (!cache?.budget) return cache;

  const categories = patchBudgetItemInCategories(cache.budget.categories, itemId, patch);
  if (categories === cache.budget.categories) return cache;
  return { ...cache, budget: { ...cache.budget, categories } };
}

export function setBudgetLockedInCache(
  cache: BudgetSummaryCache | undefined,
  locked: boolean
): BudgetSummaryCache | undefined {
  if (!cache?.budget) return cache;
  return { ...cache, budget: { ...cache.budget, locked } };
}

export function removeBudgetItemFromCache(
  cache: BudgetSummaryCache | undefined,
  itemId: number
): BudgetSummaryCache | undefined {
  if (!cache?.budget) return cache;

  const budget = structuredClone(cache.budget);
  for (const category of budget.categories) {
    for (const subCategory of category.sub_categories) {
      subCategory.items = removeItemFromTree(subCategory.items, itemId);
    }
  }
  return { ...cache, budget };
}

export function addBudgetItemToCache(
  cache: BudgetSummaryCache | undefined,
  subCategoryId: number,
  item: BudgetItem
): BudgetSummaryCache | undefined {
  if (!cache?.budget) return cache;

  const budget = structuredClone(cache.budget);
  for (const category of budget.categories) {
    const subCategory = category.sub_categories.find(
      (sub) => sub.budget_sub_category_id === subCategoryId
    );
    if (subCategory) {
      const normalizedItem = {
        ...item,
        children: item.children ?? [],
        fringes: item.fringes ?? [],
      };

      if (typeof normalizedItem.budget_item_id === "number") {
        const existing = findItemInTree(subCategory.items, normalizedItem.budget_item_id);
        if (existing) {
          Object.assign(existing, normalizedItem);
          return { ...cache, budget };
        }
      }

      if (item.parent_item_id) {
        const parent = findItemInTree(subCategory.items, item.parent_item_id);
        if (parent) {
          parent.children = [...(parent.children ?? []), normalizedItem];
          return { ...cache, budget };
        }
      }

      subCategory.items = [...subCategory.items, normalizedItem];
      return { ...cache, budget };
    }
  }
  return cache;
}

/**
 * Apply an optimistic reorder to the cached budget categories list.
 * `order` is the new sequence of category ids.
 */
export function reorderBudgetCategoriesInCache(
  cache: BudgetSummaryCache | undefined,
  order: number[]
): BudgetSummaryCache | undefined {
  if (!cache?.budget || order.length === 0) return cache;

  const byId = new Map(
    cache.budget.categories.map((category) => [category.budget_category_id, category])
  );
  const reordered = order
    .map((id) => byId.get(id))
    .filter((category): category is BudgetCategory => !!category);

  if (reordered.length !== cache.budget.categories.length) return cache;

  const budget = structuredClone(cache.budget);
  budget.categories = reordered;
  return { ...cache, budget };
}

/**
 * Apply an optimistic reorder to a cached sub-category sibling list.
 * `order` is the new sequence of sub-category ids within one parent category.
 */
export function reorderBudgetSubCategoriesInCache(
  cache: BudgetSummaryCache | undefined,
  order: number[]
): BudgetSummaryCache | undefined {
  if (!cache?.budget || order.length === 0) return cache;

  const orderSet = new Set(order);
  const budget = structuredClone(cache.budget);

  for (const category of budget.categories) {
    const matches = category.sub_categories.filter((subCategory) =>
      orderSet.has(subCategory.budget_sub_category_id)
    );
    if (matches.length !== order.length || matches.length !== category.sub_categories.length) {
      continue;
    }

    const byId = new Map(
      category.sub_categories.map((subCategory) => [
        subCategory.budget_sub_category_id,
        subCategory,
      ])
    );
    category.sub_categories = order
      .map((id) => byId.get(id))
      .filter((subCategory): subCategory is BudgetSubCategory => !!subCategory);
    return { ...cache, budget };
  }

  return cache;
}

/**
 * Apply an optimistic reorder to the cached budget. `order` is the new sequence
 * of sibling item ids (as produced by the reorder util); only the matched
 * sibling list is reordered, everything else is left untouched.
 */
export function reorderBudgetItemsInCache(
  cache: BudgetSummaryCache | undefined,
  order: number[]
): BudgetSummaryCache | undefined {
  if (!cache?.budget || order.length === 0) return cache;

  const orderSet = new Set(order);
  const budget = structuredClone(cache.budget);

  const reorderSiblings = (items: BudgetItem[]): BudgetItem[] => {
    const matches = items.filter((item) => orderSet.has(item.budget_item_id));
    let reordered = items;
    if (matches.length === order.length && matches.length === items.length) {
      const byId = new Map(items.map((item) => [item.budget_item_id, item]));
      reordered = order.map((id) => byId.get(id)).filter((item): item is BudgetItem => !!item);
    }
    return reordered.map((item) => ({
      ...item,
      children: item.children?.length ? reorderSiblings(item.children) : (item.children ?? []),
    }));
  };

  for (const category of budget.categories) {
    for (const subCategory of category.sub_categories) {
      subCategory.items = reorderSiblings(subCategory.items);
    }
  }
  return { ...cache, budget };
}

function findItemInTree(items: BudgetItem[], itemId: number): BudgetItem | null {
  for (const item of items) {
    if (item.budget_item_id === itemId) return item;
    const child = findItemInTree(item.children ?? [], itemId);
    if (child) return child;
  }
  return null;
}

function removeItemFromTree(items: BudgetItem[], itemId: number): BudgetItem[] {
  return items
    .filter((item) => item.budget_item_id !== itemId)
    .map((item) => ({
      ...item,
      children: removeItemFromTree(item.children ?? [], itemId),
    }));
}

export function findBudgetItemInCache(
  cache: BudgetSummaryCache | undefined,
  itemId: number
): BudgetItem | null {
  if (!cache?.budget) return null;
  for (const category of cache.budget.categories) {
    for (const subCategory of category.sub_categories) {
      const item = findItemInTree(subCategory.items, itemId);
      if (item) return item;
    }
  }
  return null;
}

export function findSubCategoryInCache(
  cache: BudgetSummaryCache | undefined,
  subCategoryId: number
): BudgetSubCategory | null {
  if (!cache?.budget) return null;
  for (const category of cache.budget.categories) {
    const sub = category.sub_categories.find((s) => s.budget_sub_category_id === subCategoryId);
    if (sub) return sub;
  }
  return null;
}

export function findCategoryContainingSubCategory(
  categories: BudgetCategory[],
  subCategoryId: number
): BudgetCategory | null {
  for (const category of categories) {
    if (category.sub_categories.some((sub) => sub.budget_sub_category_id === subCategoryId)) {
      return category;
    }
  }
  return null;
}
